import React, { useState } from 'react';
import axios from 'axios';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import Chatbot from '@/components/shared/Chatbot';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Shield, CheckCircle, XCircle, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CertificateVerificationPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    dob: '',
    cert_number: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.dob || !formData.cert_number) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const response = await axios.post(`${API}/verify-certificate`, {
        name: formData.name,
        dob: formData.dob,
        cert_number: formData.cert_number
      });

      setResult(response.data);
      
      if (response.data.success) {
        toast.success('Certificate Verified Successfully!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Verification failed. Please try again.');
      setResult({ success: false, message: 'Server error. Please try again later.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="hero-gradient py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
            <Shield className="h-10 w-10 text-blue-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Certificate <span className="text-blue-600">Verification</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Verify your Iqra Skills certificate instantly. Enter your details below to check certificate authenticity.
          </p>
        </div>
      </section>

      {/* Verification Form */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl border-2 border-slate-200 shadow-lg p-8" data-testid="verification-form">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Enter Certificate Details
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">Candidate Name *</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name as on certificate"
                  required
                  data-testid="name-input"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="dob">Date of Birth (DD/MM/YYYY) *</Label>
                <Input
                  id="dob"
                  type="text"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  placeholder="DD/MM/YYYY"
                  pattern="\d{2}/\d{2}/\d{4}"
                  required
                  data-testid="dob-input"
                  className="mt-1"
                />
                <p className="text-sm text-slate-500 mt-1">Format: DD/MM/YYYY (e.g., 01/01/1995)</p>
              </div>

              <div>
                <Label htmlFor="cert_number">Certificate Number *</Label>
                <Input
                  id="cert_number"
                  type="text"
                  value={formData.cert_number}
                  onChange={(e) => setFormData({ ...formData, cert_number: e.target.value.toUpperCase() })}
                  placeholder="IQRA-2025-0001"
                  required
                  data-testid="cert-number-input"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 py-6 text-lg"
                data-testid="verify-button"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <Search className="mr-2 h-5 w-5" />
                    Verify Certificate
                  </>
                )}
              </Button>
            </form>

            {/* Result Display */}
            {result && (
              <div className={`mt-8 p-6 rounded-lg border-2 ${
                result.success 
                  ? 'bg-emerald-50 border-emerald-500' 
                  : 'bg-red-50 border-red-500'
              }`} data-testid="verification-result">
                <div className="flex items-start">
                  {result.success ? (
                    <CheckCircle className="h-8 w-8 text-emerald-600 mr-3 flex-shrink-0" />
                  ) : (
                    <XCircle className="h-8 w-8 text-red-600 mr-3 flex-shrink-0" />
                  )}
                  
                  <div className="flex-1">
                    <h3 className={`text-xl font-bold mb-3 ${
                      result.success ? 'text-emerald-900' : 'text-red-900'
                    }`}>
                      {result.success ? 'Certificate Verified Successfully!' : 'Verification Failed'}
                    </h3>
                    
                    {result.success ? (
                      <div className="space-y-2 text-slate-700">
                        <p><strong>Name:</strong> {result.details.name}</p>
                        <p><strong>Course:</strong> {result.details.course}</p>
                        <p><strong>Certificate Number:</strong> {result.details.cert_number}</p>
                        <p><strong>Issue Date:</strong> {result.details.issue_date}</p>
                        {result.details.expiry_date && (
                          <p><strong>Valid Until:</strong> {result.details.expiry_date}</p>
                        )}
                        <p><strong>Status:</strong> 
                          <span className={`ml-2 px-3 py-1 rounded-full text-sm font-semibold ${
                            result.details.status === 'valid' 
                              ? 'bg-emerald-100 text-emerald-700'
                              : result.details.status === 'expired'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {result.details.status.toUpperCase()}
                          </span>
                        </p>
                      </div>
                    ) : (
                      <p className="text-red-700">{result.message}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Info Section */}
          <div className="mt-12 bg-blue-50 rounded-xl p-6 border border-blue-200">
            <h3 className="text-lg font-bold text-slate-900 mb-3">How to Verify?</h3>
            <ol className="space-y-2 text-slate-700">
              <li>1. Enter your full name exactly as it appears on the certificate</li>
              <li>2. Provide your date of birth in DD/MM/YYYY format</li>
              <li>3. Enter your certificate number (found on your certificate)</li>
              <li>4. Click 'Verify Certificate' to check authenticity</li>
            </ol>
            <p className="text-sm text-slate-600 mt-4">
              <strong>Note:</strong> If you're facing issues, please contact us at info@iqraskills.org or call +91 9521484003
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </div>
  );
};

export default CertificateVerificationPage;